import React from "react";
import {
  PlusOutlined,
  DownloadOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import TableHeaderActions from "./TableHeaderActions";

/**
 * Employee table component
 * Implements Single Responsibility Principle by focusing only on employee data display
 */
const EmployeeTable = ({
  employeeData,
  selectedRowKeys,
  setSelectedRowKeys,
  searchValue,
  isFetching,
  onAddEmployee,
  onBulkImport,
  onEditEmployee,
  onViewEmployee,
  onDeleteEmployee,
  onDownloadProfile,
  rawEmployeeData,
  tabs,
  activeTab,
  onTabChange,
  onBulkDelete,
  onBulkEdit,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  paginationInfo = {},
}) => {
  // Define columns for employee data

  // console.log('EmployeeTable rendering with employeeData:', employeeData);
  const employeeColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250, // Increased width for name column
      render: (text, record) => {
        // console.log('Rendering Name column for record:', record);
        return (
          <div className="flex items-center">
            {record.avatar && (
              <img
                src={record.avatar}
                alt={text}
                className="w-8 h-8 rounded-full mr-3 object-cover"
              />
            )}
            <div className="flex flex-col justify-center">
              <span className="font-medium">{text}</span>
              <span className="text-[10px]">{record.employeeId}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
    },
    {
      title: "Branch",
      dataIndex: "branchs",
      key: "branchs",
      width: 280,
      render: (locations) => {
        if (!locations || !Array.isArray(locations)) {
          return "N/A";
        }

        return locations.length > 0
          ? locations.map((r) => r.name).join(", ")
          : "N/A";
      },
    },
    {
      title: "Team",
      dataIndex: "teams",
      key: "teams",
      width: 250,
      render: (teams) => {
        if (!teams || !Array.isArray(teams)) {
          return "N/A";
        }

        return teams.length > 0 ? teams.map((r) => r.name).join(", ") : "N/A";
      },
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "roles",
      width: 200,
      render: (roles) => {
        if (!roles || !Array.isArray(roles)) {
          return "N/A";
        }

        return roles.length > 0 ? roles.map((r) => r.name).join(", ") : "N/A";
      },
    },

    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   width: 100,
    //   render: (status) => (
    //     <span
    //       className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
    //         status === "Active"
    //           ? "bg-green-100 text-green-700"
    //           : "bg-red-100 text-red-700"
    //       }`}
    //     >
    //       <span
    //         className={`w-2 h-2 rounded-full mr-2 ${
    //           status === "Active" ? "bg-green-500" : "bg-red-500"
    //         }`}
    //       ></span>
    //       {status}
    //     </span>
    //   ),
    // },
  ];

  // Action buttons for employee table
  const actionButtons = [
    {
      label: "Add Employee",
      type: "primary",
      icon: <PlusOutlined />,
      onClick: onAddEmployee,
      className: "bg-orange-500 border-orange-500 hover:bg-orange-600",
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    onSelectAll: (selected, selectedRows, changeRows) => {
      // console.log('Select all:', selected, selectedRows, changeRows);
    },
  };

  // Header actions when rows are selected
  const headerActions = [
    {
      label: "Bulk Edit",
      icon: <EditOutlined />,
      type: "primary",
      onClick: () => onBulkEdit && onBulkEdit(selectedRowKeys),
      disabled: selectedRowKeys.length === 0,
    },
    {
      label: "Bulk Delete",
      icon: <DeleteOutlined />,
      type: "default",
      danger: true,
      onClick: () => onBulkDelete && onBulkDelete(selectedRowKeys),
      disabled: selectedRowKeys.length === 0,
    },
  ];

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
  };

  // Row actions for employee table
  const rowActions = [
    {
      label: "View Details",
      icon: <SearchOutlined />,
      onClick: (record) => {
        const originalEmployee = rawEmployeeData?.find(
          (emp) => emp.id === record.id,
        );
        onViewEmployee(record, originalEmployee);
      },
    },
    {
      label: "Edit Employee",
      icon: <EditOutlined />,
      onClick: (record) => {
        const originalEmployee = rawEmployeeData?.find(
          (emp) => emp.id === record.id,
        );
        onEditEmployee(record, originalEmployee);
      },
    },
    {
      label: "Delete Employee",
      icon: <DeleteOutlined />,
     onClick: (record) => onDeleteEmployee(record.id, record.name),
      danger: true,
    },
  ];

  return (
    <div>
      {/* Header actions when rows are selected */}
      {/* <TableHeaderActions
        selectedCount={selectedRowKeys.length}

        actions={headerActions}
        onClearSelection={handleClearSelection}
        totalCount={paginationInfo.total || employeeData.length}
      /> */}

      <CustomDataTable
        title="Staff Management"
        data={employeeData}
        columns={employeeColumns}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        showSearch={true}
        searchPlaceholder="Search employees..."
        searchValue={searchValue}
        searchMode="server"
        onServerSearch={onSearchChange}
        actionButtons={actionButtons}
        showFilter={false}
        showCSV={true}
        onFilterClick={() => console.log("Filter clicked")}
        onCSVClick={() => console.log("CSV clicked")}
        rowSelection={rowSelection}
        selectedRowKeys={selectedRowKeys}
        rowActions={rowActions}
        footerInfo={{
          left: `${selectedRowKeys.length} of ${employeeData.length} row(s) selected.`,
        }}
        loading={isFetching}
        pagination={true}
        // pageSize={10}
        pageSizeOptions={["10", "20", "50", "100"]}
        paginationMode="server"
        currentPage={paginationInfo.current_page || 1}
        totalItems={paginationInfo.total || 0}
        serverPageSize={paginationInfo.per_page || 10}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

export default EmployeeTable;
